import { useEffect, useMemo, useRef, useState } from "react";

import axios from "axios";
import { ChartStatus, useChartsContext } from "contexts";
import debounce from "lodash/debounce";
import { RigFracChartRequestType } from "types";

import { IChartResult } from "models/model";

// Lean Rig/Frac endpoint (EVA-76) — NOT the wells/facilities /chart route. Both
// Activity Over Time and Recent Activity POST here, discriminated by chartType.
// REACT_APP_CHART_SERVICE already ends in /api/v1 (same base the /chart route uses).
const rigFracActivityEndpoint = `${process.env.REACT_APP_CHART_SERVICE}/rigFracChart`;

const postRigFracActivityData = async (
  postData: RigFracChartRequestType,
  signal?: AbortSignal
): Promise<IChartResult> => {
  const { data } = await axios.post<IChartResult>(rigFracActivityEndpoint, postData, {
    signal
  });
  return data;
};

// Mirrors usePostChartData: 300ms debounce + AbortController supersession, with
// loading ownership left to the superseding fetch on cancel.
export const usePostRigFracActivityData = (
  postData: Nullable<RigFracChartRequestType>
) => {
  const { setStatus } = useChartsContext();
  const [latestData, setLatestData] = useState<IChartResult | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<Nullable<Error>>(null);

  const abortControllerRef = useRef<Nullable<AbortController>>(null);

  const debouncedFetch = useMemo(
    () =>
      debounce(async (postData: RigFracChartRequestType) => {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        let isSuperseded = false;
        try {
          setLoading(true);
          const data = await postRigFracActivityData(postData, controller.signal);
          setLatestData(data);
          setError(null);
        } catch (err) {
          if (axios.isCancel(err) || (err as Error)?.name === "AbortError") {
            // Superseded by a newer fetch — leave loading to that fetch.
            isSuperseded = true;
            return;
          }
          setError(err as Error);
        } finally {
          if (!isSuperseded) setLoading(false);
        }
      }, 300),
    []
  );

  useEffect(() => {
    // `loading` is owned by the SUPERSEDING fetch, not by this cleanup: aborting
    // below makes the in-flight catch set isSuperseded, which skips
    // setLoading(false) in its finally, on the assumption a replacement request
    // will clear it. That holds only while `postData` never goes non-null -> null
    // while mounted — true today (the sole null producer is the controller's
    // `if (!isRigFrac) return null`, and isRigFrac is constant for the widget's
    // lifetime; switching chart type unmounts RigFracChartWrapper). If a caller
    // ever starts withholding the request from a mounted widget, this leaves the
    // spinner stuck forever and needs an explicit setLoading(false) here.
    if (postData) {
      debouncedFetch(postData);
    }
    return () => {
      debouncedFetch.cancel();
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [postData, debouncedFetch]);

  useEffect(() => {
    setStatus(
      loading ? ChartStatus.Loading : error ? ChartStatus.Error : ChartStatus.Idle
    );
  }, [loading, error, setStatus]);

  return { data: latestData, loading, error };
};
